import {
  ConfiguratorDraftPayloadSchema,
  ConfiguratorDraftResponseSchema,
  ConfiguratorPhotoResponseSchema,
  type ConfiguratorDraftPayload,
  type ConfiguratorDraftResponse,
  type ConfiguratorPhotoQuery,
  type ConfiguratorPhotoResponse,
} from '../contracts/configurator';
import type { RequestFn } from './client';

/**
 * Namespace `client.configurator` — обёртки над эндпоинтами Block 6
 * (Phase 1 ADR-0015 §Q6, Phase 9+ Edge Worker).
 *
 * Phase 1: MSW echo handler в `apps/web/src/mocks/configurator-handlers.ts`
 * принимает payload и возвращает `{success, draftId}`. Реальная persistence —
 * Phase 9+ Edge worker → региональный Postgres `configurator_drafts` table
 * (P-008 region routing по `users.region` из JWT).
 *
 * ── auth ────────────────────────────────────────────────────────────────
 * `saveDraft` — `auth: true` (Bearer JWT). Без header'а MSW handler возвращает
 * 401, что симулирует expired/missing JWT. App.tsx mount fallback (Phase 4
 * Step 3) на 401 → `logout()` → guest mode.
 *
 * `getPhoto` — `auth: false` (publicly cached). Phase 9+ R2 bucket
 * `/assets/configurator-photos/` отдаёт контуры мебели edge-кэшем с долгим
 * Cache-Control. Не sensitive — Global ID типа мебели не ПДн.
 *
 * ── Phase 9+ extension ──────────────────────────────────────────────────
 * Block 6 Phase 9+ может добавить:
 *   - `getDraft(id)` — recovery после refresh.
 *   - `listDrafts()` — для Block 11 «Личный кабинет → Мои проекты».
 *   - `deleteDraft(id)` — UX «удалить из истории».
 * Каждое расширение — amendment этого ADR-0015 + extension этого namespace.
 */

export interface ConfiguratorApi {
  /**
   * Sensitive: требует JWT (401 для гостя). Echo в Phase 1, real persistence
   * Phase 9+ → региональный Postgres.
   */
  saveDraft(payload: ConfiguratorDraftPayload): Promise<ConfiguratorDraftResponse>;
  /**
   * Public: возвращает URL контура мебели для type. Phase 1 placeholder
   * (`/configurator-photos/{type}.png`), Phase 9+ R2 signed URL.
   */
  getPhoto(query: ConfiguratorPhotoQuery): Promise<ConfiguratorPhotoResponse>;
}

export function createConfiguratorApi(request: RequestFn): ConfiguratorApi {
  return {
    saveDraft: async (payload) => {
      // Client-side guard: parse через .strict() ДО отправки. Защита от
      // случайной отправки невалидного payload (например, type=null или
      // duplicate slot ids — что не должно происходить, но runtime
      // pre-validation ловит баги в UI коде раньше, чем сервер).
      //
      // `async` обёртка обязательна — `parse()` бросает синхронно ДО
      // возврата Promise, что мешает caller'у отлавливать ошибку через
      // `.catch` / `await rejects.toThrow()`. С `async` любой sync throw
      // конвертируется в rejected Promise.
      const parsed = ConfiguratorDraftPayloadSchema.parse(payload);
      return await request({
        method: 'POST',
        path: '/api/configurator/draft',
        body: parsed,
        responseSchema: ConfiguratorDraftResponseSchema,
        auth: true,
      });
    },
    getPhoto: (query) =>
      request({
        method: 'GET',
        path: '/api/configurator/photo',
        query: { type: query.type },
        responseSchema: ConfiguratorPhotoResponseSchema,
      }),
  };
}
